
import { Component, Vue, Watch } from 'vue-property-decorator'
import User from '@/types/user'
import Building from '@/types/building'
import ServiceComments from '@/views/service/comments/ServiceComments.vue'
import ServiceCommentBlock from '@/views/service/comments/ServiceCommentBlock.vue'
import ServiceComment from '@/types/serviceComment'
import RelatedServiceItems from '@/views/service/RelatedServiceItems.vue'
import Issue, { IssueTypes } from '@/types/issue'
import moment from 'moment/moment'
import ServiceObjectInfo from '@/views/service/ServiceObjectInfo.vue'
import Filelist from '@/components/media/Filelist.vue'
import axios from 'axios'
import ls from '@/ls'
import { ServiceClasses, ServiceStates, SortedStates, Status } from '@/types/serviceItem'
import ActivityLogTable from '@/components/ActivityLogTable.vue'
import ServiceDetails from '@/views/service/ServiceDetails.vue'
import ServiceContactInfo from '@/views/service/ServiceContactInfo.vue'
import ServiceBuildingSelector from '@/views/service/ServiceBuildingSelector.vue'
import SubscribeButton from '@/components/base/SubscribeButton.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import PreviewContainer from '@/components/media/PreviewContainer.vue'
import SaveButton from '@/components/base/SaveButton.vue'
import SimpleUploader from '@/components/media/SimpleUploader.vue'
import { Base64MediaFile, getBase64Async } from '@/types/media'
import MultiSolverSelector from '@/views/service/components/MultiSolverSelector.vue'
import PrioritySelector from '@/views/service/components/PrioritySelector.vue'
import Tagger from '@/components/base/Tagger.vue'
import ServicePlanner from '@/views/service/components/ServicePlanner.vue'
import { can } from '@/globalFunctions'
import { realtimeService, RealtimeUnsubscribe } from '@/services/realtime'

@Component({
  methods: { can },
  components: {
    ServicePlanner,
    Tagger,
    PrioritySelector,
    MultiSolverSelector,
    SimpleUploader,
    SaveButton,
    PreviewContainer,
    PageHeader,
    SubscribeButton,
    ServiceBuildingSelector,
    ServiceContactInfo,
    ServiceDetails,
    ServiceObjectInfo,
    ActivityLogTable,
    Filelist,
    IssueObjectInfo: ServiceObjectInfo,
    RelatedServiceItems,
    ServiceCommentBlock,
    ServiceComments,
    ServiceCreateComment: ServiceComments
  }
})

export default class IssueEdit extends Vue {
  serviceClasses = ServiceClasses
  serviceStates = ServiceStates
  sortedStates = SortedStates
  issueTypes = IssueTypes
  moment = moment
  realtimeSubscription?: RealtimeUnsubscribe
  config = {
    entity: 'issue',
    entities: 'issues'
  }

  settings = {
    routeNames: {
      list: 'issues',
      show: 'issueShow',
      edit: 'issueEdit'
    },
    routeParamKey: 'issueId',
    permissionNames: {
      show: 'issues.view',
      edit: 'issues.edit'
    }
  }

  downloadOptionsModalVisible = false
  exportTypes: string[] = []
  currentTab: string = ls.get('issueEditActiveTab') || 'details'
  tabs = [
    {
      key: 'details',
      tab: 'Details'
    },
    {
      key: 'related',
      tab: 'Gerelateerd'
    },
    {
      key: 'activity',
      tab: 'Geschiedenis'
    }
  ]

  contact = {
    name: null,
    phone: null,
    email: null
  }

  search = {
    company: ''
  }

  downloading = false

  @Watch('currentItem.project.id')
  onProjectChanged (): void {
    if (this.currentItem.project.id === 0) return
    this.$store.dispatch('issueStore/getManagers', this.currentItem.project.id)
  }

  @Watch('currentItem.status')
  onStatusChanged (status: number, previousStatus: number|undefined): void {
    if (previousStatus === undefined || status !== ServiceStates.Opgelost || status === previousStatus) return
    this.$store.dispatch('issueStore/completeRunningWorkflowSolvers')
  }

  onTabChange (key: string): void {
    this.currentTab = key
  }

  mounted (): void {
    this.getIssue()
    this.$store.dispatch('issueStore/getCompanies')
    this.registerRealtimeEvents()
  }

  beforeDestroy (): void {
    this.unregisterRealtimeEvents()
  }

  registerRealtimeEvents (): void {
    if (!this.authUser) return

    this.realtimeSubscription = realtimeService.listenPrivate('issues', '.issue.updated', (e: { issue: Pick<Issue, 'id'> }) => {
      if (e.issue.id === this.currentItem.id) {
        this.getIssue()
      }
    })
  }

  unregisterRealtimeEvents (): void {
    if (this.realtimeSubscription) {
      this.realtimeSubscription()
      this.realtimeSubscription = undefined
    }
  }

  getIssue () : void {
    this.$store.dispatch('issueStore/setCurrent', this.$route.params[this.settings.routeParamKey])
  }

  destroyed (): void {
    this.$store.dispatch('issueStore/clearCurrent')
  }

  onContactError (error: {field: string, message: string}): void {
    if (!error || !error.field || !error.message) {
      this.$store.commit('issueStore/removeError', 'email')
      return
    }

    if (error.field === 'email') {
      this.$store.commit('issueStore/addError', 'email')
    }
  }

  get canEdit (): boolean {
    return can(this.settings.permissionNames.edit)
  }

  get canSave (): boolean {
    return this.canEdit && !this.saving && this.errors.length === 0
  }

  get canSeeTags (): boolean {
    return can('issues.tags.view')
  }

  get canEditTags (): boolean {
    return can('issues.tags.edit') && this.canEdit
  }

  get currentItem (): Issue {
    return this.$store.getters['issueStore/current']
  }

  get building (): Building {
    return this.currentItem.building || new Building()
  }

  get comments (): ServiceComment[] {
    return this.currentItem.comments || [new ServiceComment()]
  }

  get managers (): { id: number, active: boolean, full_name: string, role: string }[] {
    return this.$store.getters['issueStore/managers']
  }

  canMarkAsReady (item: Issue): boolean {
    const externalType = this.issueTypes.EXTERNAL_INSPECTION.value
    const internalType = this.issueTypes.INTERNAL_INSPECTION.value
    return (item.type === externalType && this.authUser.id !== item.user.id) || item.type === internalType
  }

  save (): void {
    this.$store.dispatch('issueStore/save', this.currentItem)
  }

  saveAndReturn (): void {
    this.save()
    this.$router.push({ name: this.settings.routeNames.list })
  }

  onTagsUpdated (tags: string[]): void {
    this.$store.dispatch('issueStore/setTags', tags)
  }

  getStatus (): Status {
    return this.serviceClasses.find(s => s.id === this.currentItem.status) || this.serviceClasses[0]
  }

  onBuildingSelected (id: number): void {
    this.$store.dispatch('issueStore/setBuilding', id)
  }

  onProjectSelected (id: number): void {
    this.$store.dispatch('issueStore/setProject', id)
  }

  onManagerSelected (id: number): void {
    this.$store.dispatch('issueStore/setManager', id)
  }

  onCommentSaved (comment: ServiceComment): void {
    if (comment.waitingForResponse) {
      this.$store.dispatch('issueStore/setStatus', this.serviceStates.InAfwachting)
    }
  }

  previewFiles:Base64MediaFile[] = []

  async handleMedia (file: File): Promise<void> {
    const previewFile: Base64MediaFile = new Base64MediaFile(file)
    previewFile.url = await getBase64Async(file)
    previewFile.name = file.name.replace(/\.[^/.]+$/, '')
    this.previewFiles = [...[previewFile], ...this.previewFiles]
    await this.$store.dispatch('issueStore/setMedia', this.previewFiles)
  }

  deleteFile (target: { index: number, file: File}) : void {
    axios.delete(process.env.VUE_APP_API_URL + '/issues/' + this.currentItem.id + '/media/' + this.currentItem.files[target.index].id)
    this.$store.dispatch('issueStore/removeFile', target.index)
  }

  generatePDF (): void {
    this.downloading = true
    const payload = {
      types: this.exportTypes
    }
    axios.post(`${process.env.VUE_APP_API_URL}/issues/${this.currentItem.id}/export`, payload, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'Export_Servicemelding_' + this.currentItem.id + '.pdf'
        link.click()
      }).finally(() => {
        this.downloading = false
      })

    this.exportTypes = []
    this.downloadOptionsModalVisible = false
  }

  filterOptions (input: string, option: { componentOptions: { children: { text: string }[] } }): boolean {
    return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  get isRental (): boolean {
    const rentalStates: number[] = [5, 6, 7]
    return rentalStates.includes(this.currentItem.building?.state)
  }

  get authUser (): User {
    return this.$store.getters['authStore/authUser']
  }

  get loading (): boolean {
    return this.$store.getters['issueStore/loading'].item
  }

  get saving (): boolean {
    return this.$store.getters['issueStore/loading'].save
  }

  get errors (): string[] {
    return this.$store.getters['issueStore/errors']
  }
}
